import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { baseUrl } from '../../utils/axios'
import { formatParams, handleErrors } from 'utils/helpers'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  ICreateRequestPayload,
  ICurrentUser,
  IDecisionPayload,
  IRequestRecord,
  IRequestsSlice,
  IRequestUser,
} from 'types/requests.interface'

const initialState: IRequestsSlice = {
  isLoading: false,
  currentUser: null,
  employees: { data: [], totalCount: 0, totalPages: 0 },
  requests: { data: [], totalCount: 0, totalPages: 0 },
}

export const getRequestsMe = createAsyncThunk('requests/me', async (_, thunkAPI) => {
  try {
    const resp: AxiosResponse<ICurrentUser> = await baseUrl.get('hr/requests/me')
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getRequestEmployees = createAsyncThunk(
  'requests/employees',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IRequestUser>> = await baseUrl.get(
        'hr/requests/employees',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getRequests = createAsyncThunk('requests/getRequests', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IRequestRecord>> = await baseUrl.get(
      'hr/requests',
      { params },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const createRequest = createAsyncThunk(
  'requests/createRequest',
  async (body: ICreateRequestPayload, thunkAPI) => {
    try {
      const resp: AxiosResponse<IRequestRecord> = await baseUrl.post('hr/requests', body)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const approveRequest = createAsyncThunk(
  'requests/approveRequest',
  async ({ id, note }: IDecisionPayload, thunkAPI) => {
    try {
      const resp: AxiosResponse<IRequestRecord> = await baseUrl.post(
        `hr/requests/${id}/approve`,
        { note },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const rejectRequest = createAsyncThunk(
  'requests/rejectRequest',
  async ({ id, note }: IDecisionPayload, thunkAPI) => {
    try {
      const resp: AxiosResponse<IRequestRecord> = await baseUrl.post(
        `hr/requests/${id}/reject`,
        { note },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const cancelRequest = createAsyncThunk('requests/cancelRequest', async (id: number, thunkAPI) => {
  try {
    const resp: AxiosResponse<IRequestRecord> = await baseUrl.post(`hr/requests/${id}/cancel`)
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

const requestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {},
  extraReducers: {
    [getRequestsMe.fulfilled.type]: (state: IRequestsSlice, { payload }: { payload: ICurrentUser }) => {
      state.currentUser = payload
    },
    [getRequestsMe.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getRequestEmployees.fulfilled.type]: (
      state: IRequestsSlice,
      { payload }: { payload: IPaginatedResponse<IRequestUser> },
    ) => {
      state.employees = payload
    },
    [getRequestEmployees.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getRequests.pending.type]: (state) => {
      state.isLoading = true
    },
    [getRequests.fulfilled.type]: (
      state: IRequestsSlice,
      { payload }: { payload: IPaginatedResponse<IRequestRecord> },
    ) => {
      state.requests = payload
      state.isLoading = false
    },
    [getRequests.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createRequest.fulfilled.type]: () => {
      toast.success('Request submitted')
    },
    [createRequest.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [approveRequest.fulfilled.type]: () => {
      toast.success('Request approved')
    },
    [approveRequest.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [rejectRequest.fulfilled.type]: () => {
      toast.success('Request rejected')
    },
    [rejectRequest.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [cancelRequest.fulfilled.type]: () => {
      toast.success('Request cancelled')
    },
    [cancelRequest.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
  },
})

export default requestsSlice.reducer
